/// <amd-module name="Repos/Views/CommitHub/Components/CommitFilesTab" />
import * as React from "react";
import { DiscussionFilterOptions } from "Repos/Artifact/Discussion/ArtifactDiscussionThreadService";
import { ChangesExplorerView } from "Repos/Changes/Components/ChangesExplorerView";
import { BranchStats } from "Repos/Changes/Contracts";
import { ChangesContext } from "Repos/ChangesCommon/ChangesContext";
import { getGitNavigationService } from "Repos/ChangesCommon/ChangesNavigationService";
import { IChangeCollection } from "Repos/ChangesCommon/Contracts";
import { GitVersionDescriptor } from "Repos/Common/Contracts/Git";
import { ICommitsTableProps } from "Repos/Common/Contracts/History";
import { getReposRoutingService } from "Repos/Common/ReposRoutingService";
import { IGitFilesService } from "Repos/Files/GitFilesService";
import { ICommitViewService } from "Repos/Views/CommitHub/CommitViewService";
import * as Resources from "Repos/Views/CommitHub/Resources";
import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { useComponentContext, WrappedComponent } from "VSS/Platform/Layout";
import { ExpandableButton } from "VSSUI/Button";
import { Callout } from "VSSUI/Callout";
import { IExpandable } from "VSSUI/Expandable";
import { getKeywordFilterItem } from "VSSUI/Filter";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { AggregateItemProvider } from "VSSUI/Utilities/AggregateItemProvider";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IOffset, IOrigin, IPoint, Location } from "VSSUI/Utilities/Position";
import { FilesFilterKeys } from "../CommitViewService";
import { getDiscussionIntegrationService } from "../Discussion/CommitDiscussionIntegrationService";
import { getCommitDiscussionService } from "../Discussion/CommitDiscussionThreadService";

export const CommitFilesTab = (): JSX.Element => {
    const { pageContext } = useComponentContext();
    const reposRoutingService = getReposRoutingService(pageContext);
    const commitViewService = pageContext.getService<ICommitViewService>("ICommitViewService");
    const commitDiscussionService = getCommitDiscussionService(pageContext);
    const filesService = pageContext.getService<IGitFilesService>("IGitFilesService");
    const filterStore = commitViewService.getFilesFilter();
    const navigationService = getGitNavigationService(pageContext);

    const [selection] = React.useState(new DropdownSelection());

    return (
        <div className="flex-column flex-grow">
            <Observer firstThreadAdded={commitDiscussionService.getFirstThreadAdded()} branchStats={commitViewService.getBranchStats()}>
                {(props: { firstThreadAdded: boolean; branchStats?: BranchStats }) => {
                    const associatedPR = props.branchStats && props.branchStats.associatedPRStats;
                    // Show first thread added warning if the commit has an associated pull request
                    return props.firstThreadAdded && associatedPR ? (
                        <MessageCard severity={MessageCardSeverity.Warning} onDismiss={() => commitDiscussionService.setFirstThreadAdded(false)}>
                            <FormatComponent format={Resources.DiscussionThreadAddedWarning}>
                                <FPSLink href={associatedPR.url}>
                                    !{associatedPR.id}: {associatedPR.title}
                                </FPSLink>
                            </FormatComponent>
                        </MessageCard>
                    ) : null;
                }}
            </Observer>
            <Observer changes={commitViewService.getChanges()} allFiles={commitViewService.getAllChangedPaths()}>
                {(props: { changes: IChangeCollection<GitVersionDescriptor>; allFiles: string[] }) => {
                    const filterProvider = new AggregateItemProvider<IListBoxItem<{}>>();
                    const files: IListBoxItem<boolean>[] = props.allFiles.map(file => ({ id: file, text: file }));
                    filterProvider.push(files);
                    filterProvider.push(DiscussionFilterOptions);

                    return (
                        <ChangesContext.Provider value={{ filesService, navigationService, viewService: commitViewService }}>
                            <ChangesExplorerView
                                addDiffKeyboardShortcuts={true}
                                changes={props.changes}
                                discussionService={getDiscussionIntegrationService(pageContext)}
                                filterProps={{
                                    filterStore,
                                    filterItems: [
                                        getKeywordFilterItem(filterStore, 400, files),
                                        {
                                            filterItemKey: FilesFilterKeys.discussions,
                                            id: "files-discussions-filter",
                                            items: DiscussionFilterOptions,
                                            name: Resources.FilesFilterDiscussions
                                        }
                                    ],
                                    items: filterProvider,
                                    selection,
                                    showActiveFilterResetButton: true,
                                    showFilterOnText: false
                                }}
                                getFileUrl={path =>
                                    reposRoutingService.getCommitUrl(
                                        commitViewService.getRepository().name,
                                        commitViewService.getRepository().project.name,
                                        commitViewService.getCommitId(),
                                        undefined,
                                        path,
                                        "files"
                                    )
                                }
                                getItemMenuItems={(item, treeDiffIntegration, rootDiffIntegration) =>
                                    commitViewService.getItemMenuItems(item, treeDiffIntegration, rootDiffIntegration)
                                }
                                selectedThreadId={commitViewService.getSelectedThreadId()}
                                preFilterContent={<CommitParentSelector />}
                                isMergeCommit={commitViewService.getParents().length > 1}
                                commitTooManyChanges={commitViewService.getCommitTooManyChanges()}
                            />
                        </ChangesContext.Provider>
                    );
                }}
            </Observer>
        </div>
    );
};

const CommitParentSelector = (): JSX.Element | null => {
    const { pageContext } = useComponentContext();
    const commitViewService = pageContext.getService<ICommitViewService>("ICommitViewService");

    const parents = commitViewService.getParents();
    const selectedParentId = commitViewService.getSelectedParentId().value;
    const selectedParent = parents.find(p => p.id === selectedParentId);

    if (!selectedParent || selectedParent.isInitialCommit) {
        return null;
    }

    return (
        <ExpandableButton
            anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
            className="flex-grow"
            dropdownOrigin={{ horizontal: Location.start, vertical: Location.start }}
            renderCallout={(
                dropdown: IExpandable,
                dropdownId: string,
                anchorElement?: HTMLElement,
                anchorOffset?: IOffset,
                anchorOrigin?: IOrigin,
                anchorPoint?: IPoint,
                dropdownOrigin?: IOrigin
            ): JSX.Element => {
                commitViewService.loadParentCommitDetails();
                return (
                    <Callout
                        anchorElement={anchorElement}
                        anchorOffset={anchorOffset}
                        anchorOrigin={anchorOrigin}
                        anchorPoint={anchorPoint}
                        blurDismiss={true}
                        calloutOrigin={dropdownOrigin}
                        contentShadow={true}
                        escDismiss={true}
                        focuszoneProps={{
                            circularNavigation: true,
                            defaultActiveElement: ".repos-commit-parents-dropdown",
                            focusOnMount: true
                        }}
                        lightDismiss={true}
                        onDismiss={dropdown.collapse}
                    >
                        <div className="repos-commit-parents-dropdown v-scroll-auto custom-scrollbar scroll-auto-hide no-outline" tabIndex={-1}>
                            <WrappedComponent<ICommitsTableProps>
                                wrappedType="repos-commits-table"
                                dependencies={["ms.vss-code-web.commits-list"]}
                                wrappedProps={{
                                    commits: parents.map(p => p.commit),
                                    onActivate: (event, commit) => {
                                        if (!(event as React.MouseEvent<HTMLElement>).ctrlKey) {
                                            const parent = parents.find(p => p.displayCommitId === commit.commit.commitId);
                                            if (parent) {
                                                event.preventDefault();
                                                commitViewService.setSelectedParentId(parent.id);
                                                dropdown.collapse();
                                            }
                                        }
                                    },
                                    showHeader: false
                                }}
                            />
                        </div>
                    </Callout>
                );
            }}
        >
            {selectedParent.isMergeSummary ? selectedParent.displayName : format(Resources.ParentSelectorFormat, selectedParent.displayName)}
        </ExpandableButton>
    );
};
